import { useCallback, useEffect, useState } from 'react';
import { PropertiesService } from '@services/requests';

interface UsePropertyImagesResult {
  imageUrls: string[];
  loading: boolean;
  /** Re-reads the presigned URLs, for after an upload or a delete. */
  refetch: () => void;
}

/**
 * Ensures image URLs use HTTPS to avoid mixed-content blocking
 * and normalizes redundant path slashes.
 */
function normalizeImageUrl(url: string): string {
  let normalized = url.replace(/^http:\/\//, 'https://');
  normalized = normalized.replace(/([^:]\/)\/+/g, '$1');
  return normalized;
}

/**
 * Resolves image URLs from the presigned endpoint, falling back to raw keys.
 */
async function fetchImageUrls(agencyId: string, propertyId: string, fallbackKeys: string[]): Promise<string[]> {
  try {
    const res = await PropertiesService.getPropertyImageUrls(agencyId, propertyId);
    const urls = (res.urls ?? []).map(normalizeImageUrl);
    return urls.length > 0 ? urls : fallbackKeys;
  } catch {
    return fallbackKeys;
  }
}

/**
 * Fetches presigned view URLs for property images.
 *
 * Falls back to raw file keys when the presigned endpoint is unavailable
 * (e.g. local dev without S3). The backend local-images proxy handles
 * S3 key paths in development.
 *
 * @param agencyId - Agency identifier
 * @param propertyId - Property identifier
 * @param fallbackKeys - Raw file keys from property.media.images
 * @param coverImage - Optional cover image key to pin first
 * @returns Resolved image URLs and loading state
 */
export function usePropertyImages(
  agencyId: string,
  propertyId: string,
  fallbackKeys: string[],
  coverImage?: string,
): UsePropertyImagesResult {
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  /*
   * An upload does not change any of the effect's inputs — same agency, same
   * property, and `fallbackKeys` comes from a property record that has not
   * been refetched yet. Without a counter to bump, the effect never re-runs
   * and the photo the user just uploaded stays invisible until a reload.
   */
  const [reloadToken, setReloadToken] = useState(0);
  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);

  useEffect(() => {
    if (!agencyId || !propertyId) {
      setImageUrls([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchImageUrls(agencyId, propertyId, fallbackKeys).then((urls) => {
      if (!cancelled) {
        setImageUrls(urls);
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [agencyId, propertyId, fallbackKeys.length, reloadToken]);

  return { imageUrls, loading, refetch };
}
